/**
 * Premium SLA (UI task success / fail) metrics for the new help center, a.k.a. hubs.
 *
 * Hub routes render through the platform `@atlassian/hub` packages and emitted neither
 * `view-help-center` nor `request-create`. This mounts the same `Experience` with the same
 * payload and screen, so hub traffic is separated in reporting by the `helpCenterCategory`
 * attribute the analytics client already adds, not by a different metric:
 * - Success: hub pages have no help center notifiers, so `HubPremiumSlaElementsProvider`
 *   supplies one per rendered element.
 * - Failure: hub failures are caught by the platform's `HubErrorBoundary`, which reports
 *   to `HubErrorObserverContext`.
 */

import React, { useCallback, useContext, useMemo } from 'react';

import { Experience, ExperienceContext, ExperienceSuccess } from '@helpcenter/analytics-components';
import type { ScreenName } from '@helpcenter/constants';
import { ExperienceName } from '@helpcenter/constants';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { Route } from 'react-resource-router';

import { AnalyticsContext, ScreenTypes } from '@atlassian/analytics-web-react';

import {
	ExtensionMode,
	ProductDependenciesContext,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import {
	type HubErrorReport,
	HubErrorObserverContext,
} from '@atlassian/company-hub-common/controllers/hub-error-observer';

import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';

/** Gates the whole hub emission, so it can be rolled out gradually and killed if it misreports. */
const HUB_PREMIUM_SLA_METRICS = 'help_center_hub_premium_sla_metrics';

export const isHubPremiumSlaMetricsEnabled = (): boolean =>
	FeatureGates.checkGate(HUB_PREMIUM_SLA_METRICS);

/** The page itself failed to render, so neither journey can complete. */
const PAGE_LEVEL_ERROR_IDS = new Set(['hub-page', 'route-with-hub-shell']);

const JSM_HELP_EXTENSION_KEY = 'jsm-help';

/**
 * Fail the home page but not create request: a customer can still reach the request
 * form when these fail.
 */
const HOME_PAGE_ONLY_ERROR_IDS = new Set([
	'spotlight',
	'carousel',
	'cards',
	'links-menu',
	'hub-section',
	'banner',
]);

/**
 * Explicit allowlist rather than the platform's `shouldForceError`, which is also set
 * for editor-only boundaries and unset for the banner.
 */
export const countsAsPremiumSlaFailure = (id: string, journey?: ExperienceName): boolean => {
	if (PAGE_LEVEL_ERROR_IDS.has(id) || id === JSM_HELP_EXTENSION_KEY) {
		return true;
	}

	return journey === ExperienceName.HELP_CENTER && HOME_PAGE_ONLY_ERROR_IDS.has(id);
};

interface PremiumSlaErrorObserverProps {
	children?: React.ReactNode;
	journey: ExperienceName;
}

const PremiumSlaErrorObserver = ({ children, journey }: PremiumSlaErrorObserverProps) => {
	const { onFailure } = useContext(ExperienceContext);

	const observeError = useCallback(
		({ error, id }: HubErrorReport) => {
			const fetchError = error as unknown as FetchError;

			// Only 5xx counts. 4xx and network errors are excluded, as in the older help center.
			if (!countsAsPremiumSlaFailure(id, journey) || isFetchNetworkOrClientError(fetchError)) {
				return;
			}

			onFailure({
				errorMessage: error.message,
				errorName: error.name,
				unhandledError: true,
				errorLocation: id.startsWith('hub-') ? id : `hub-${id}`,
				status: fetchError.status,
			});
		},
		[journey, onFailure],
	);

	return (
		<HubErrorObserverContext.Provider value={observeError}>
			{children}
		</HubErrorObserverContext.Provider>
	);
};

interface HubPremiumSlaElementsProviderProps {
	children?: React.ReactNode;
	extensionKey?: string;
	mode?: ExtensionMode;
}

/**
 * Reports a rendered hub element to the surrounding experience, so `taskSuccess` can fire.
 * Uses `experienceStartComponent` because the platform renders it for every element,
 * whereas `experienceSuccessComponent` is only rendered by a few.
 */
export const HubPremiumSlaElementsProvider = ({
	children,
	extensionKey,
	mode,
}: HubPremiumSlaElementsProviderProps): React.JSX.Element => {
	const productDependencies = useProductDependencies();
	// Only the published renderer is measured; editing a hub is not a customer journey.
	const isEnabled = isHubPremiumSlaMetricsEnabled() && mode === ExtensionMode.RENDERER;

	// Keyed on the extension key alone: a changing identity would remount the element.
	const experienceStartComponent = useMemo(() => {
		const location = `hub-element-${extensionKey || 'unknown'}`;
		const HubElementRendered = () => <ExperienceSuccess location={location} />;
		HubElementRendered.displayName = 'HubElementRendered';

		return HubElementRendered;
	}, [extensionKey]);

	const value = isEnabled
		? { ...productDependencies, experienceStartComponent }
		: productDependencies;

	return (
		<ProductDependenciesContext.Provider value={value}>
			{children}
		</ProductDependenciesContext.Provider>
	);
};

/**
 * Mounts the Premium SLA experience for a hub route.
 *
 * `screenName` must be the screen the older help center uses for the equivalent page, so the
 * emitted event's `source` matches and the two are comparable. Without it the events inherit
 * the app-wide `portalUnknown` fallback. `AnalyticsContext` rather than the `AnalyticsScreen`
 * the older help center routes use, because hubs already own their page load timing.
 */
export const withHubPremiumSlaExperience = (
	route: Route,
	journey: ExperienceName,
	screenName: ScreenName,
): Route => {
	const Component = route.component;

	if (!isHubPremiumSlaMetricsEnabled() || !Component) {
		return route;
	}

	const HubRouteWithPremiumSla = (props: React.ComponentProps<typeof Component>) => (
		<AnalyticsContext sourceName={screenName} sourceType={ScreenTypes.SCREEN}>
			<Experience name={journey}>
				<PremiumSlaErrorObserver journey={journey}>
					<Component {...props} />
				</PremiumSlaErrorObserver>
			</Experience>
		</AnalyticsContext>
	);

	return { ...route, component: HubRouteWithPremiumSla as Route['component'] };
};
